import _ from 'lodash'
import getLocalizedTexts from '../localization'
import {
    // Constants
    USERS_READ_FROM_FORAGE,
    USERS_FORGET_USERS,
    USERS_LOGIN_USER,
    USERS_LOGOUT_USER,
    TOGGLE_PINNED,
    TOGGLE_MULTISTART,
    SET_TOKENS,
    SET_PROFILE,
    REMOVE_AUTOLAUNCH,
    SET_SERVER_ORDER,

    // Actions
    setMultistart,
    setOrder,
    setPinned,
    setProfiles,
    setTokens,
    removeUserProfile,
    usersChanged,
    userAdded,
    usersAdded,
    setTexts,

    // Selectors
    getUsersForStorage,
    // getOldUsersForStorage,
    getServerProfiles,
    getOrder,
    isUserPinned,
    shouldUserMultiStart,
} from '../data'
import { actions as imported, constants as c } from '../imports'

const broadcastChange = (store) => {
    const users = getUsersForStorage(store.getState())
    store.dispatch(usersChanged(users))
}

const log = (store, text) => {
    store.dispatch(imported.log(text))
}

const readFromStorage = (store, multiStart, profiles, tokens, order) => {
    store.dispatch(setMultistart(multiStart))
    store.dispatch(setPinned(order))
    store.dispatch(setProfiles(profiles))
    store.dispatch(setTokens(tokens))
    store.dispatch(setOrder(order))
}

const readOldFromStorage = (store, list, order) => {
    // Now I want to break down this users object
    // into small pieces and send them away to their respective reducers...
    //     const users = {
    //         list: {
    //             tq: {
    //                 testuser_1: {
    //                     username: 'testuser_1',
    //                     hash: '',
    //                     multiLaunch: true,
    //                     profile: '',
    //                     remember: true,
    //                     tokens: {
    //                         accessToken: '',
    //                         refreshToken: '',
    //                     },
    //                 },
    //                 testuser_2: {
    //                     username: 'testuser_2',
    //                     profile: '',
    //                     shell: true,
    //                 },
    //             },
    //         },
    //         order: {
    //             tq: ['testuser_1'],
    //         },
    //     }
    // If the list is missing, we are done
    if (list == null) return

    // We need to populate the following:
    const data = {
        multiStart: {},
        pinned: {},
        profiles: {},
        tokens: {},
    }

    // Now loop through the users.list:
    _.forOwn(list, (userList, serverId) => {
        if (!_.isEmpty(userList)) {
            data.multiStart[serverId] = []
            data.pinned[serverId] = []
            data.profiles[serverId] = {}
            data.tokens[serverId] = {}

            // Now loop through the userList:
            _.forOwn(userList, (user, userId) => {
                // Profile
                if (user.profile !== 'Default') {
                    data.profiles[serverId][userId] = user.profile
                }

                // Is this a logged in user
                if (!user.shell) {
                    // Multi start
                    if (user.multiLaunch) {
                        data.multiStart[serverId].push(userId)
                    }
                    // Pinned
                    data.pinned[serverId].push(userId)
                    // Profile
                    if (user.profile !== 'Default') {
                        data.profiles[serverId][userId] = user.profile
                    }
                    // Tokens
                    data.tokens[serverId][userId] = {
                        access: user.tokens.accessToken,
                        refresh: user.tokens.refreshToken,
                    }
                }
            })
        }
    })

    // And now we dispatch all the relevant actions:
    if (!_.isEmpty(data.multiStart)) {
        store.dispatch(setMultistart(data.multiStart))
    }

    if (!_.isEmpty(data.pinned)) {
        store.dispatch(setPinned(data.pinned))
    }

    if (!_.isEmpty(data.profiles)) {
        store.dispatch(setProfiles(data.profiles))
    }

    if (!_.isEmpty(data.tokens)) {
        store.dispatch(setTokens(data.tokens))
    }

    store.dispatch(setOrder(order))
}

const UsersMiddleware = store => next => (action) => {
    switch (action.type) {
        // VERIFIED!
        case USERS_READ_FROM_FORAGE: {
            const result = next(action)
            const { multiStart, profiles, tokens, order, list } = action.payload

            if (multiStart != null) {
                readFromStorage(store, multiStart, profiles, tokens, order)
            } else {
                readOldFromStorage(store, list, order)
                broadcastChange(store) // to update the cache to the new way
            }

            // Broadcast userIds of logged in users
            store.dispatch(usersAdded(getOrder(store.getState())))

            return result
        }

        // VERIFIED!
        case USERS_FORGET_USERS: {
            const result = next(action)
            store.dispatch(usersChanged({}))
            return result
        }

        // Locale has changed, get the localized text
        case c.LOCALE_CHANGED: {
            const result = next(action)
            const text = getLocalizedTexts(action.payload)
            store.dispatch(
                setTexts({
                    addAccount: text.addAccount,
                    addAccountTooltip: text.addTooltip,
                    play: text.play,
                    playTooltip: text.playTooltip,
                    pinTooltip: text.pin,
                    unPinTooltip: text.unpin,
                    startTooltip: text.startClient,
                    stopTooltip: text.stopClient,
                    profileTooltip: text.profile,
                    logOutTooltip: text.remove,
                    errorAutoRefresh: text.auto_refresh_fail,
                    errorRefreshing: text.refreshing_error,
                }),
            )
            return result
        }

        // VERIFIED!
        case USERS_LOGIN_USER: {
            const result = next(action)
            const { serverId, user } = action.payload
            broadcastChange(store)
            // Broadcast userId of user that was just added
            store.dispatch(userAdded(serverId, user.userId))
            log(
                store,
                `Logging in '${user.userId}' with pin ${
                    user.pinned ? 'enabled' : 'disabled'
                } (${serverId})`,
            )
            return result
        }

        // VERIFIED!
        case USERS_LOGOUT_USER: {
            const result = next(action)
            const { serverId, userId } = action.payload
            broadcastChange(store)
            log(store, `Logging out ${userId} (${serverId})`)
            return result
        }

        // VERIFIED!
        case TOGGLE_PINNED: {
            const result = next(action)
            const { serverId, userId } = action.payload
            const pinned = isUserPinned(store.getState(), serverId, userId)
            broadcastChange(store)
            // Log changes to pinned state
            log(store, `Pinned state set to ${pinned} for ${userId} on ${serverId}`)
            return result
        }

        // VERIFIED!
        case TOGGLE_MULTISTART: {
            const result = next(action)
            const { serverId, userId } = action.payload
            const multi = shouldUserMultiStart(store.getState(), serverId, userId)
            broadcastChange(store)
            // Log changes to multi-launch state
            log(
                store,
                `Multi-launch ${multi ? 'enabled' : 'disabled'} for ${userId} on ${serverId}`,
            )
            return result
        }

        case SET_TOKENS: {
            const result = next(action)
            broadcastChange(store)
            return result
        }

        // VERIFIED!
        case SET_PROFILE: {
            const result = next(action)
            const { serverId, userId, profile } = action.payload
            broadcastChange(store)
            log(store, `User profile update: ${userId} will use '${profile}' on ${serverId}`)
            return result
        }

        // VERIFIED!
        case REMOVE_AUTOLAUNCH: {
            const result = next(action)
            broadcastChange(store)
            return result
        }

        case SET_SERVER_ORDER: {
            const result = next(action)
            broadcastChange(store)
            return result
        }

        // The list of server profiles available for the server has been updated
        // Now I need to go through all users on the given server and
        // if the uesr is using a profile not present in the list,
        // then remove the profile
        // todo: We could instead keep it in store and reply with 'Default' if
        // the profile is not in the list for the server...
        case c.SERVER_PROFILES_CHANGED: {
            const { serverId, profiles } = action.payload
            const result = next(action)

            // Get a list of user profiles for the given server
            const users = getServerProfiles(store.getState(), serverId)
            // users = { testuser_1: '', testuser_2: '' }

            _.forOwn(users, (profile, userId) => {
                if (profiles.indexOf(profile) === -1) {
                    store.dispatch(removeUserProfile(serverId, userId))
                }
            })
            return result
        }

        default:
            return next(action)
    }
}

export default UsersMiddleware



// WEBPACK FOOTER //
// ./src/features/Users/middleware/index.js